import React, { useMemo, useState } from 'react'
import Select from 'react-select'
import Creatable from 'react-select/creatable'
import { Drawer, Tabs, Dropdown, Menu } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { useToggle, timeDiff, useListData, usePet, stringifyDate } from '../../modules'

import Receive from '../reusable/receive'
import Confirm from '../reusable/confirm'
import Modal from "../reusable/modal"
import Patient from '../reusable/patient'

import NewPatient from '../modals/newPatient'
import NewWard from '../modals/newWard'

const AdmissionDetails = ({ toggle, visible, wards, patient, onPerform, onCreate }) => {
    const [bed, setBed] = useState()
    const [ward, setWard] = useState()
    const [status, setStatus] = useState({ label: 'Regular Admission', value: 'Regular Admission' })
    const [indicator, setIndicator] = useState()
    const [beds, setBeds] = useState([])
    const [wardName, setWardName] = useState()
    
    const [newWard, toggleNewWard] = useToggle()

    const { user } = useSelector(state => state.application)
    const patientSchemes = patient?.schemes ?? patient?.schemesData ?? []
    const shaScheme = useMemo(
        () => patientSchemes.find(item => item?.active !== false && item?.scheme?.operatedBy?.isSha),
        [patientSchemes],
    )
    const mainScheme = useMemo(
        () => patientSchemes.find(item => item?.main) ?? patientSchemes[0],
        [patientSchemes],
    )

    const onComplete = ({ data }, notif, ops) => {
        notif('Patient has been admitted', ops('success'))
        setTimeout(toggle, 300)
        onPerform()
    }

    const [{ loading: admLoading }, admitPatient] = usePet('admitPatient', {
        id: patient?._id,
        indicator: indicator?.value,
        status: status?.value,
        ward: ward?._id,
        bed: bed?._id
    }, { notify: true, callback: onComplete, manual: true, load: { msg: 'Recording admission...' } })
    
    const [, getWard] = usePet('ward', { id: ward?._id }, { notify: true, callback: ({ data }) => setBeds(data.beds), manual: true })

    const createNewWard = name => {
        setWardName(name)
        toggleNewWard()
    }

    const onCreateWard = data => {
        setWard({ label: data.name, value: data._id, ...data })
        onCreate(data)
    }

    const selectWard = ward => {
        setWard(ward)
        setBed(null)
        getWard()
    }

    let indicators = ['Medical', 'Surgical', 'Obs/Gyn', 'Paediatric', 'Maternity', 'Eye', 'Nursery/Newborn', 'Orthopaedic', 'Isolation', 'Amenity', 'Psychiatry', 'ICU', 'Renal', 'Other']
    let statuses = ['Regular Admission', 'Returned Abscondee', 'Returned AMA']

    return (
        <Modal
            w={ 400 }
            title="Admission Details"
            toggle={toggle}
            visible={visible}>
            {
                newWard &&
                <NewWard
                    visible={ newWard }
                    name={ wardName }
                    onPerform={ onCreateWard }
                    toggle={ toggleNewWard } />
            }
            <div className="flex flex-wrap mb-6">
                <div className="w-full my-3">
                    <div className={`rounded-sm border px-4 py-3 ${shaScheme ? 'border-emerald-200 bg-emerald-50' : mainScheme ? 'border-blue-200 bg-blue-50' : 'border-yellow-200 bg-yellow-50'}`}>
                        <p className={`text-xs font-semibold uppercase mb-1 ${shaScheme ? 'text-emerald-800' : mainScheme ? 'text-blue-800' : 'text-yellow-800'}`}>
                            Coverage context
                        </p>
                        {
                            shaScheme
                                ? <>
                                    <p className="text-sm text-emerald-900 mb-1">This patient has an active SHA-linked scheme: {shaScheme?.scheme?.name}.</p>
                                    <p className="text-xs text-emerald-800 mb-0">Proceed knowing this admission is likely to flow through SHA inpatient claim handling and final discharge consent requirements.</p>
                                </>
                                : mainScheme
                                    ? <>
                                        <p className="text-sm text-blue-900 mb-1">Main scheme: {mainScheme?.scheme?.name}</p>
                                        <p className="text-xs text-blue-800 mb-0">Review the patient’s billing context if this admission should follow a specific insurer or inpatient payment path.</p>
                                    </>
                                    : <>
                                        <p className="text-sm text-yellow-900 mb-1">No patient scheme is attached yet.</p>
                                        <p className="text-xs text-yellow-800 mb-0">Admission can proceed, but billing and claim handling may need manual follow-up if insurance details are missing.</p>
                                    </>
                        }
                    </div>
                </div>
                <div className="w-full my-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Status
                    </label>
                    <Select
                        value={status}
                        onChange={setStatus}
                        styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                        options={ statuses?.map(stat => ({ label: stat, value: stat })) }
                        placeholder="Admission Status" />
                </div>
                <div className="w-full my-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Admission Type
                    </label>
                    <Select
                        value={indicator}
                        onChange={setIndicator}
                        styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                        options={ indicators?.map(ind => ({ label: ind, value: ind })) }
                        placeholder="Admission Type" />
                </div>
                <div className="w-full my-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Ward
                    </label>
                    {
                        user?.issuperuser ?
                            <Creatable
                                value={ward}
                                onChange={selectWard}
                                onCreateOption={createNewWard}
                                styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                                options={wards?.map(ward => ({ label: ward.name, value: ward._id, ...ward }))}
                                placeholder="Select Or Add New Ward" />
                            :
                            <Select
                                value={ward}
                                onChange={selectWard}
                                styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                                options={wards?.map(ward => ({ label: ward.name, value: ward._id, ...ward }))}
                                placeholder="Select Ward" />
                    }
                </div>
                <div className="w-full my-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                        Bed
                    </label>
                    {
                        user?.issuperuser ?
                            <Creatable
                                value={bed}
                                onChange={setBed}
                                isDisabled={!ward}
                                onCreateOption={createNewWard}
                                styles={{ 
                                    input: styles => ({ ...styles, minWidth: '10em' }),
                                    option: (styles, { isDisabled }) => ({ 
                                        ...styles, 
                                        backgroundColor: isDisabled ? undefined : styles.backgroundColor,
                                        color: isDisabled ? 'gray' : styles.color,
                                        cursor: isDisabled ? 'not-allowed' : 'default' 
                                    })
                                }}
                                options={beds?.map(bed => ({ label: `${bed.name}${ bed.occupied ? ' (Occupied)' : ''}`, value: bed._id, isDisabled: bed.occupied, ...bed }))}
                                placeholder="Select Or Add New Bed"
                                 />
                            :
                            <Select
                                value={bed}
                                onChange={setBed}
                                isDisabled={!ward}
                                styles={{ 
                                    input: styles => ({ ...styles, minWidth: '10em' }),
                                    option: (styles, { isDisabled }) => ({ 
                                        ...styles, 
                                        backgroundColor: isDisabled ? undefined : styles.backgroundColor,
                                        color: isDisabled ? 'gray' : styles.color,
                                        cursor: isDisabled ? 'not-allowed' : 'default' 
                                    })
                                }}
                                options={beds?.map(bed => ({ label: `${bed.name}${ bed.occupied ? ' (Occupied)' : ''}`, value: bed._id, isDisabled: bed.occupied, ...bed }))}
                                placeholder="Select Bed" />
                    }
                </div>
            </div>
            <div className="flex justify-end">
                <button onClick={ () => Confirm({
                    title: 'Admit patient?',
                    message: 'Please confirm before performing this action',
                    okText: 'Admit patient',
                    onOk: admitPatient
                }) } type="submit" className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">Proceed</button>
            </div>
        </Modal>
    )
}

const AdmitPatient = ({ onCreate, onClose }) => {
    const [string, setString] = useState()
    const [pt, setPatient] = useState()
    const [wards, setWards] = useState()

    const history = useHistory()

    const [visible, toggleVisible] = useToggle(true)
    const [details, toggleDetails] = useToggle()
    const [newpatient, toggleNewPatient] = useToggle()

    const [
        { loading: pLoading, error: pError, fetch: fetchPatients },
        { list: patients },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('patientsForAdmission', { string }, { notify: true }, 'patients')
    
    const [, getWards] = usePet('wards', { active: true, limit: 50 }, { notify: true, callback: ({ data }) => setWards(data.wards) })

    const refresh = () => {
        navigate(1)
        fetchPatients()
    }

    const search = str => {
        setString(str)
        refresh()
    }

    const close = () => {
        toggleVisible()
        onClose()
        setTimeout(() => history.goBack(), 300)
    }

    const admit = ptn => {
        setPatient(ptn)
        toggleDetails()
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            // footer={
            //     <div className="w-full text-right">
            //         <button className="text-white mr-2 hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Admit Patient</button>                    
            //         <button className="text-white mr-2 hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Close</button>
            //     </div>
            // }
            width="40%">
            {
                details &&
                <AdmissionDetails
                    toggle={ toggleDetails }
                    patient={ pt }
                    visible={ details }
                    wards={ wards }
                    onCreate={ data => {
                        getWards()
                        onCreate(data)
                    }}
                    onPerform={ close }/>
            }
            <p className="font-extrabold text-3xl mb-0">Admit Patient</p>
            <div className="mt-3">
                <div className="sm:flex sm:justify-between sm:items-center mb-4">
                    <input
                        onChange={e => search(e.target.value)}
                        className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        id="grid-search"
                        type="text"
                        placeholder="Patient Name, OP/IP No." />
                    <button onClick={ toggleNewPatient } className="btn ml-2 py-2.1 hisani-btn-green-light text-white">New Patient</button>
                </div>

                <NewPatient
                    onPerform={ refresh }
                    toggle={ toggleNewPatient }
                    visible={ newpatient }/>
                <Receive
                    loading={pLoading}
                    error={pError}>
                    {
                        patients?.length > 0 ?
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full">
                                    {/* Table header */}
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2">
                                                <div className="font-semibold text-center">No</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Patient Name</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-center"></div>
                                            </th>
                                        </tr>
                                    </thead>
                                    {/* Table body */}
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            patients.map((patient, key) =>
                                                <tr>
                                                    <td className="p-2">
                                                        <div className="text-center">
                                                            { ((page - 1) * limit) + (key + 1)}.
                                                        </div>
                                                    </td>
                                                    <td className="p-2">
                                                        <Patient patient={ patient } />
                                                    </td>
                                                    <td className="p-2">
                                                        {
                                                            patient.admission ?
                                                            <div className="flex space-x-2 pl-1 items-center">
                                                                <div className="w-2 h-2 rounded-full bg-yellow-500 shadow"></div>
                                                                <p className="text-yellow-500 text-xs italic mb-0">Currently admited</p>
                                                            </div>
                                                            :
                                                            <button onClick={ () => admit(patient) } className="btn ml-2 py-1 hisani-btn-green-light text-white">Admit</button>
                                                        }
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                            :
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">You can add a new patient by clicking above</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
        </Drawer>
    )
}

export default AdmitPatient
